import { Link, useParams, Navigate } from 'react-router-dom';
import { Phone, MapPin, CheckCircle2, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { COMPANY, SERVICES, WHY_US } from '../mock/data';
import { CITY_BY_SLUG, SERVICE_CITY_COMBOS, SERVICE_SLUGS, CITIES } from '../data/locations';

export default function CityPage() {
  const { citySlug } = useParams();
  const city = CITY_BY_SLUG[citySlug];
  if (!city) return <Navigate to="/" replace />;

  const canonical = `https://betterfenceanddeck.org/fence-contractor/${city.slug}`;
  const title = `Fence Contractor in ${city.fullName} | Better Fence & Deck`;
  const description = `Better Fence & Deck installs wood, vinyl, chain-link, and ornamental iron fences plus custom decks in ${city.fullName}. Free estimates. Call ${COMPANY.phone}.`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: `Better Fence & Deck — ${city.name}`,
    telephone: '+1-318-344-7190',
    url: canonical,
    image: `https://betterfenceanddeck.org/gallery/01-hero-dusk-pool.jpg`,
    address: { '@type': 'PostalAddress', addressLocality: city.name, addressRegion: city.state, addressCountry: 'US' },
    geo: { '@type': 'GeoCoordinates', latitude: city.lat, longitude: city.lng },
    priceRange: '$$',
    areaServed: { '@type': 'City', name: city.fullName },
  };

  const serviceCombos = SERVICE_CITY_COMBOS.filter(([, c]) => c === city.slug).map(([s]) => s);

  return (
    <div className="bg-ink text-cream min-h-screen">
      <SEO title={title} description={description} canonical={canonical} jsonLd={jsonLd} />
      <Navbar />

      {/* Hero */}
      <section className="pt-40 pb-16 md:pb-24 bg-ink relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="/gallery/01-hero-dusk-pool.jpg" alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/80 to-ink/40" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold font-bold">
            <MapPin className="w-4 h-4" /> {city.fullName}
          </div>
          <h1 className="mt-4 font-display uppercase text-4xl md:text-6xl leading-[1.02] tracking-tight">
            Fence Contractor in <span className="text-gold">{city.name}</span>
          </h1>
          <p className="mt-5 max-w-2xl text-cream/80 text-lg leading-relaxed">{city.blurb}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="#quote" className="btn-primary">Get Free Estimate <ArrowRight className="w-4 h-4" /></a>
            <a href={`tel:${COMPANY.phoneRaw}`} className="btn-ghost"><Phone className="w-4 h-4" /> {COMPANY.phone}</a>
          </div>
        </div>
      </section>

      {/* Services in this city */}
      <section className="py-16 md:py-24 bg-ink-soft border-y border-white/5">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="eyebrow">Services in {city.name}</div>
          <h2 className="section-title">Every fence and deck type, installed locally.</h2>
          <p className="section-lede">
            We install and repair every major fence type in {city.fullName} — wood privacy, vinyl, chain-link, and ornamental iron. We also build custom decks, covered porches, and pergolas.
          </p>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s) => (
              <div key={s.id} className="rounded-md border border-white/10 bg-ink-panel p-5 hover:border-gold/50 transition-colors">
                <h3 className="font-display uppercase text-xl text-cream tracking-tight">{s.title}</h3>
                <p className="mt-2 text-sm text-cream/70">{s.blurb}</p>
                <div className="mt-2 text-[11px] uppercase tracking-widest text-gold">Starting at {s.from}</div>
              </div>
            ))}
          </div>

          {serviceCombos.length > 0 && (
            <div className="mt-12">
              <div className="text-[11px] uppercase tracking-widest text-gold font-bold">Dedicated pages</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {serviceCombos.map((s) => (
                  <Link key={s} to={`/services/${s}/${city.slug}`} className="px-4 py-2 rounded-md text-xs uppercase tracking-widest font-bold border border-white/15 text-cream hover:border-gold hover:text-gold transition-colors">
                    {SERVICE_SLUGS[s].title} in {city.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Neighborhoods */}
      {city.landmarks && city.landmarks.length > 0 && (
        <section className="py-16 md:py-24 bg-ink">
          <div className="max-w-4xl mx-auto px-5 md:px-8">
            <div className="eyebrow">Neighborhoods We Serve</div>
            <h2 className="section-title">Familiar with every corner of {city.name}.</h2>
            <p className="section-lede">
              Our crews work regularly across {city.name}. Common neighborhoods and areas include:
            </p>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
              {city.landmarks.map((n) => (
                <div key={n} className="flex items-center gap-2 text-cream/80"><CheckCircle2 className="w-4 h-4 text-gold shrink-0" /> {n}</div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why us */}
      <section className="py-16 md:py-24 bg-ink-soft border-y border-white/5">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="eyebrow">Why choose Better Fence & Deck in {city.name}</div>
          <h2 className="section-title">Locally trusted, fully insured.</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {WHY_US.slice(0, 6).map((w) => (
              <div key={w.title} className="p-5 rounded-md border border-white/10 bg-ink-panel">
                <h3 className="font-display uppercase text-lg text-cream">{w.title}</h3>
                <p className="mt-2 text-sm text-cream/65">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section id="quote" className="py-16 md:py-24 bg-ink">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <div className="eyebrow justify-center inline-flex">Get Started</div>
          <h2 className="section-title">Free estimates in {city.name}.</h2>
          <p className="section-lede mx-auto">Call or text {COMPANY.phone}, or head to our quote form. Most estimates scheduled within 24 hours.</p>
          <div className="mt-6 flex justify-center gap-3">
            <a href={`tel:${COMPANY.phoneRaw}`} className="btn-primary"><Phone className="w-4 h-4" /> {COMPANY.phone}</a>
            <Link to="/#quote" className="btn-outline">Fill Quote Form</Link>
          </div>
        </div>
      </section>

      {/* Other cities */}
      <section className="py-14 bg-ink-soft border-t border-white/5">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="text-[11px] uppercase tracking-widest text-gold font-bold">Other cities we serve</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {CITIES.filter(c => c.slug !== city.slug).map((c) => (
              <Link key={c.slug} to={`/fence-contractor/${c.slug}`} className="px-4 py-2 rounded-md text-xs uppercase tracking-widest border border-white/10 text-cream/80 hover:border-gold hover:text-gold transition-colors">
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
